import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Icon from '../components/icon';
import {
  addManagerComment,
  createManagerCommentFileLink,
  getManagerAudit,
  uploadManagerCommentFile,
} from '../api/panel-api';
import { eventLabels, formatDateTime, initials } from '../data/panel';

const FILTERS = [
  { id: 'all', label: 'Все' },
  { id: 'comments', label: 'Комментарии' },
  { id: 'system', label: 'Системные' },
];

const MONTHS = [
  'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
  'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря',
];

const FILE_LIMIT = 50 * 1024 * 1024;
const FILE_TYPES = ['png', 'jpg', 'jpeg', 'pdf'];

function personName(person) {
  if (!person || typeof person !== 'object') return '';
  return [person.first_name, person.last_name].filter(Boolean).join(' ') || person.email || '';
}

function formatWhen(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  const stamp = formatDateTime(value);
  return `${date.getDate()} ${MONTHS[date.getMonth()]}, ${stamp.time}`;
}

function isComment(event) {
  return event.category === 'comment' || event.event_type === 'manager_comment_added';
}

function afterText(event, keys) {
  const after = event.after;
  if (!after || typeof after !== 'object') return '';
  for (let i = 0; i < keys.length; i += 1) {
    const value = after[keys[i]];
    if (value) return String(value);
  }
  return '';
}

function eventBody(event) {
  if (isComment(event)) return event.comment || '';
  if (event.event_type === 'manager_moved_to_division') {
    const name = afterText(event, ['division_title', 'title', 'division', 'name']);
    if (name) return `Менеджер перемещён в дивизион «${name}»`;
  }
  return eventLabels[event.event_type] || event.event_type || '';
}

function highlight(text, query) {
  const value = String(text || '');
  const needle = String(query || '').trim();
  if (!needle || !value) return value;
  const escaped = needle.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const parts = value.split(new RegExp(`(${escaped})`, 'ig'));
  return parts.map((part, index) => (
    part.toLowerCase() === needle.toLowerCase()
      ? <b key={`${part}-${index}`}>{part}</b>
      : part
  ));
}

function formatSize(bytes) {
  if (bytes === null || bytes === undefined || bytes === '') return '';
  const n = Number(bytes);
  if (!Number.isFinite(n) || n < 0) return '';
  const mb = n / (1024 * 1024);
  const text = mb >= 10 ? String(Math.round(mb)) : mb.toFixed(1).replace('.', ',');
  return `${text} МБ`;
}

function extensionOf(name) {
  const match = String(name || '').toLowerCase().match(/\.([a-z0-9]+)$/);
  return match ? match[1] : '';
}

function isImage(format) {
  return format === 'png' || format === 'jpg' || format === 'jpeg';
}

function readBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

function FeedImage({ managerId, fileId }) {
  const [src, setSrc] = useState('');
  useEffect(() => {
    let cancelled = false;
    createManagerCommentFileLink(managerId, fileId)
      .then((data) => {
        if (!cancelled && data && data.url) setSrc(data.url);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [fileId, managerId]);
  if (!src) return <span>JPG</span>;
  return <img src={src} alt="" />;
}

function FileTile({ file, preview, managerId, onOpen, onRemove }) {
  const format = file.format || extensionOf(file.name);
  const deleted = Boolean(file.deleted_at);
  if (deleted) {
    return <span className="feed-file feed-file--gone">Файл удалён</span>;
  }
  const image = preview || (isImage(format) ? file.preview : '');
  const inner = image
    ? <img src={image} alt="" />
    : isImage(format) && managerId
      ? <FeedImage managerId={managerId} fileId={file.id} />
      : <span>PDF</span>;
  return (
    <div className="feed-file">
      {onOpen ? (
        <button type="button" className="feed-file__preview" onClick={onOpen}>
          {inner}
        </button>
      ) : (
        <div className="feed-file__preview">
          {inner}
          {onRemove ? (
            <button type="button" className="feed-file__remove" aria-label="Убрать файл" onClick={onRemove}>
              <Icon name="x" size={12} tone="inherit" />
            </button>
          ) : null}
        </div>
      )}
      <span className="feed-file__name">{file.name}</span>
      {formatSize(file.size) ? <span className="feed-file__size">{formatSize(file.size)}</span> : null}
    </div>
  );
}

function CommentDialog({ managerId, onClose, onSaved }) {
  const inputRef = useRef(null);
  const [text, setText] = useState('');
  const [files, setFiles] = useState([]);
  const [error, setError] = useState('');
  const [pending, setPending] = useState(false);

  async function addFiles(list) {
    setError('');
    const next = [];
    for (let i = 0; i < list.length; i += 1) {
      const file = list[i];
      const format = extensionOf(file.name);
      if (!FILE_TYPES.includes(format)) {
        setError('Можно прикрепить PNG, JPG или PDF.');
        return;
      }
      if (file.size > FILE_LIMIT) {
        setError('Файл больше 50 МБ.');
        return;
      }
      try {
        const content = await readBase64(file);
        const uploaded = await uploadManagerCommentFile(managerId, {
          name: file.name,
          content_base64: content,
        });
        next.push({
          ...uploaded,
          preview: isImage(uploaded.format || format) ? URL.createObjectURL(file) : '',
        });
      } catch (uploadError) {
        setError(uploadError.message);
        return;
      }
    }
    setFiles((current) => current.concat(next));
  }

  async function submit() {
    const comment = text.trim();
    if (!comment || pending) return;
    setPending(true);
    setError('');
    try {
      const body = { comment };
      if (files.length) body.file_ids = files.map((item) => item.id);
      await addManagerComment(managerId, body);
      onSaved();
    } catch (saveError) {
      setError(saveError.message);
      setPending(false);
    }
  }

  return createPortal(
    <div className="overlay overlay--front">
      <div className="dialog" role="dialog" aria-modal="true" aria-labelledby="manager-comment-title">
        <div className="dialog__head">
          <h2 id="manager-comment-title">Добавить комментарий</h2>
          <button type="button" className="icon-button" aria-label="Закрыть" onClick={onClose}>
            <Icon name="x" size={18} />
          </button>
        </div>
        <div className="dialog__body">
          <textarea
            className="comment"
            placeholder="Комментарий"
            value={text}
            onChange={(event) => setText(event.target.value)}
          />
          {files.length ? (
            <div className="feed-files">
              {files.map((file) => (
                <FileTile
                  key={file.id}
                  file={file}
                  preview={file.preview}
                  onRemove={() => setFiles((current) => current.filter((item) => item.id !== file.id))}
                />
              ))}
            </div>
          ) : null}
          <div className="feed-attach">
            <button type="button" className="btn btn--secondary btn--small" onClick={() => inputRef.current?.click()}>
              <Icon name="paperclip" size={16} />
              Прикрепить файл
            </button>
            <input
              ref={inputRef}
              type="file"
              accept=".png,.jpg,.jpeg,.pdf"
              hidden
              multiple
              onChange={(event) => {
                addFiles(Array.from(event.target.files || []));
                event.target.value = '';
              }}
            />
            <p>Необязательно. До 50 МБ, PNG, JPG или PDF</p>
          </div>
          {error ? <p className="empty">{error}</p> : null}
        </div>
        <div className="dialog__footer">
          <button type="button" className="btn btn--secondary btn--small" onClick={onClose}>Отмена</button>
          <button type="button" className="btn btn--small" disabled={pending || !text.trim()} onClick={submit}>
            Добавить
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function ManagerFeed({ managerId, canComment }) {
  const [filter, setFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [sentQuery, setSentQuery] = useState('');
  const [items, setItems] = useState([]);
  const [error, setError] = useState('');
  const [dialog, setDialog] = useState(false);
  const [reload, setReload] = useState(0);
  const canWrite = canComment && filter !== 'system';

  useEffect(() => {
    const timer = window.setTimeout(() => setSentQuery(query.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    let cancelled = false;
    setError('');
    getManagerAudit(managerId, {
      page: 1,
      perPage: 100,
      filter,
      q: sentQuery,
    })
      .then((data) => {
        if (!cancelled) setItems(data.items || []);
      })
      .catch((loadError) => {
        if (!cancelled) {
          setItems([]);
          setError(loadError.message);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [managerId, filter, sentQuery, reload]);

  async function openFile(file) {
    if (!file || file.deleted_at) return;
    try {
      const data = await createManagerCommentFileLink(managerId, file.id);
      if (data && data.url) window.open(data.url, '_blank', 'noopener,noreferrer');
    } catch (openError) {
      setError(openError.message);
    }
  }

  const emptySearch = !error && sentQuery && items.length === 0;

  return (
    <div className="manager-feed">
      <div className="manager-tab__head">
        <h2>Лента событий</h2>
      </div>
      <div className="feed-toolbar">
        <div className="feed-switch" role="tablist" aria-label="Лента событий">
          {FILTERS.map((item) => (
            <button
              key={item.id}
              type="button"
              role="tab"
              aria-selected={filter === item.id}
              className={filter === item.id ? 'feed-switch__item is-active' : 'feed-switch__item'}
              onClick={() => setFilter(item.id)}
            >
              {item.label}
            </button>
          ))}
        </div>
        <div className="feed-toolbar__actions">
          <label className="feed-search feed-search--box">
            <Icon name="search" size={16} />
            <input
              type="search"
              value={query}
              placeholder="Поиск по ленте"
              onChange={(event) => setQuery(event.target.value)}
            />
            {query ? (
              <button type="button" className="feed-search__clear" aria-label="Очистить поиск" onClick={() => setQuery('')}>
                <Icon name="x" size={14} />
              </button>
            ) : null}
          </label>
          {canWrite ? (
            <button type="button" className="btn btn--secondary" onClick={() => setDialog(true)}>
              <Icon name="plus" size={16} />
              Добавить комментарий
            </button>
          ) : null}
        </div>
      </div>
      {error ? <p className="feed-empty">{error}</p> : null}
      {emptySearch ? (
        <div className="empty managers-empty managers-empty--tab">
          <div className="empty__icon"><Icon name="filter" size={24} tone="inherit" /></div>
          <div>
            <p className="empty__title">Ничего не найдено</p>
            <p className="empty__lead">Измените запрос или очистите поиск.</p>
          </div>
          <button type="button" className="btn btn--secondary" onClick={() => setQuery('')}>Очистить поиск</button>
        </div>
      ) : null}
      {!error && !emptySearch ? (
        <div className="feed-list">
          {items.map((event) => {
            const comment = isComment(event);
            const actor = personName(event.actor) || (comment ? '' : 'Система');
            const markClass = event.actor ? 'feed-entry__mark feed-entry__mark--person' : 'feed-entry__mark';
            return (
              <article className="feed-entry" key={event.id}>
                {comment ? (
                  <span className={`feed-entry__avatar feed-entry__avatar--${(actor.charCodeAt(0) || 0) % 2}`}>
                    {initials(event.actor?.first_name, event.actor?.last_name) || '•'}
                  </span>
                ) : (
                  <span className={markClass}>
                    <Icon name="clock" size={16} />
                  </span>
                )}
                <div className="feed-entry__body">
                  <span>
                    <strong>{highlight(actor, sentQuery)}</strong>
                    {' '}
                    <time>{formatWhen(event.occurred_at)}</time>
                  </span>
                  <p className="feed-entry__text">{highlight(eventBody(event), sentQuery)}</p>
                  {comment && event.files && event.files.length ? (
                    <div className="feed-files">
                      {event.files.map((file) => (
                        <FileTile key={file.id} file={file} managerId={managerId} onOpen={() => openFile(file)} />
                      ))}
                    </div>
                  ) : null}
                </div>
              </article>
            );
          })}
        </div>
      ) : null}
      {dialog ? (
        <CommentDialog
          managerId={managerId}
          onClose={() => setDialog(false)}
          onSaved={() => {
            setDialog(false);
            setReload((value) => value + 1);
          }}
        />
      ) : null}
    </div>
  );
}

export default ManagerFeed;
